import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import AiQueryContext from '../components/ai-dashboard/AiQueryContext'
import AiResultsDisplay from '../components/ai-dashboard/AiResultsDisplay'
import { AI_API_ENDPOINTS } from '../config/api'

/**
 * AI Dashboard Page
 *
 * Full-page AI-powered query interface with:
 * - Natural language queries with location context
 * - Integrated cache browsing (examples + recent queries)
 * - Complete results display with dataSpec enhancement
 * - Data completeness indicators
 * - Deep linking support
 */
export default function AiDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentResponse, setCurrentResponse] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Load query/cache from URL on mount
  useEffect(() => {
    const cacheKey = searchParams.get('cache')
    const query = searchParams.get('query')

    if (cacheKey) {
      loadCachedQuery(cacheKey)
    } else if (query) {
      // Query will be handled by AiQueryInput component
      // which reads from URL params
    }
  }, []) // Only run on mount

  const loadCachedQuery = async cacheKey => {
    setIsProcessing(true)
    try {
      const response = await fetch(AI_API_ENDPOINTS.cachedQuery(cacheKey))
      if (response.ok) {
        const data = await response.json()
        setCurrentResponse({
          ...data.response,
          _metadata: data.metadata, // Store metadata separately
        })
      } else {
        setCurrentResponse({
          type: 'error',
          text: 'Unable to load cached query. It may have expired or been removed.',
          visualizations: [],
        })
      }
    } catch (error) {
      console.error('Error loading cached query:', error)
      setCurrentResponse({
        type: 'error',
        text: 'Failed to load cached query. Please try again.',
        visualizations: [],
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleQuerySubmit = (response, queryParams) => {
    setCurrentResponse(response)

    // Update URL for sharing
    const params = new URLSearchParams()
    if (queryParams.query) params.set('query', queryParams.query)
    if (queryParams.district) params.set('district', queryParams.district)
    if (queryParams.subcounty) params.set('subcounty', queryParams.subcounty)
    if (queryParams.parish) params.set('parish', queryParams.parish)
    if (queryParams.village) params.set('village', queryParams.village)
    if (queryParams.category) params.set('category', queryParams.category)

    setSearchParams(params)
  }

  const handleCacheClick = cacheKey => {
    // Update URL
    setSearchParams({ cache: cacheKey })

    // Load the cached query
    loadCachedQuery(cacheKey)
  }

  const handleClearResults = () => {
    setCurrentResponse(null)
    setSearchParams({})
  }

  return (
    <div className="bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Text */}
        <div className="mb-6">
          <p className="text-gray-600 text-center">
            Ask questions about your data in natural language and get AI-powered
            insights
          </p>
        </div>

        {/* Main Content - Full Width Sections */}
        <div className="space-y-6">
          {/* Query Context Section - Full Width */}
          <AiQueryContext
            onQuerySubmit={handleQuerySubmit}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            onCacheClick={handleCacheClick}
          />

          {/* Results Section - Full Width */}
          {currentResponse && (
            <AiResultsDisplay
              response={currentResponse}
              onClear={handleClearResults}
            />
          )}

          {/* Empty State */}
          {!currentResponse && !isProcessing && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Sparkles className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Ready to Explore Your Data
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Select a location and category, then ask a question to get
                AI-powered insights with visualizations and detailed analysis.
              </p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">AI-Powered Insights</p>
              <p className="text-blue-800">
                Responses are generated using AI and may occasionally contain
                inaccuracies. Always verify critical information with the
                underlying facility data. Cached results are marked with a ⚡
                badge.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
